/**
 * Public PM-facing status page.
 *
 * Shape returned by GET /public/status/:slug. No auth. The shape is
 * deliberately a SUBSET of Project — no internal cost data, no PM contact
 * lookups, no crew email/phone, no internal notes.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { AddressSchema } from './locations.js';
import { ScheduleDaySchema } from './projects.js';
import { ProjectStatusSchema, TaskStatusSchema, TaskTypeSchema, PrioritySchema } from './enums.js';
export const PublicCrewMemberSchema = z.object({
    display_name: z.string(),
    crew_role: z.string(),
    avatar_url: z.string().url().nullable(),
});
export const PublicTaskSchema = z.object({
    type: TaskTypeSchema,
    title: z.string(),
    status: TaskStatusSchema,
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
    started_at: IsoDateTimeSchema.nullable(),
    completed_at: IsoDateTimeSchema.nullable(),
    crew: z.array(PublicCrewMemberSchema),
});
export const PublicStatusResponseSchema = z.object({
    // Header
    /** The project's id — lets a signed-in viewer jump to the in-app project. The
     *  slug stays the only thing needed to VIEW; this just enables the deep link. */
    project_id: UuidSchema,
    project_title: z.string(),
    client_name: z.string(),
    priority: PrioritySchema,
    status: ProjectStatusSchema,
    // Site
    site_address: AddressSchema.nullable(),
    site_contact_name: z.string().nullable(),
    // Schedule
    requested_start_date: IsoDateTimeSchema.nullable(),
    requested_end_date: IsoDateTimeSchema.nullable(),
    completed_at: IsoDateTimeSchema.nullable(),
    /** The committed working windows the crew is booked for, one entry per day
     *  (approved → proposed → requested, first non-empty wins), sorted by date.
     *  Empty when the project carries no day-level schedule. */
    schedule: z.array(ScheduleDaySchema),
    /** When the project first entered each lifecycle stage, derived from the
     *  append-only event log (`payload.to_status`). Null for a stage not yet
     *  reached (or for legacy projects predating the event). Drives the inline
     *  "when" annotations on the public stage timeline. */
    stage_timestamps: z.object({
        scheduled: IsoDateTimeSchema.nullable(),
        en_route: IsoDateTimeSchema.nullable(),
        on_site: IsoDateTimeSchema.nullable(),
        punch: IsoDateTimeSchema.nullable(),
        pending_closeout: IsoDateTimeSchema.nullable(),
        complete: IsoDateTimeSchema.nullable(),
    }),
    /** Driving minutes to the site while a crew is en route; null otherwise or
     *  when no ETA provider is configured (ADR-0009 amended). */
    eta_minutes: z.number().int().min(0).nullable(),
    // Tasks (lean)
    tasks: z.array(PublicTaskSchema),
    // Recap (populated once status=complete)
    recap_url: z.string().url().nullable(),
    recap_pdf_url: z.string().url().nullable(),
    // For "Site is ready" two-way handoff
    pm_can_advance: z.boolean(),
});
//# sourceMappingURL=public-status.js.map